<template>
  <div class="image-panel" v-if="contentLoaded">
    <div class="image-panel__content">
      <img :src="logoUrl" class="image-panel__logo" alt="logo" />

      <div class="image-panel__main-content">
        <div class="image-panel__headline">
          <h1 class="image-panel__title">
            <TypingAnimation :quotes="quotes" />
          </h1>
          <p class="image-panel__subtitle">A smarter workspace for content-driven teams.</p>

          <!-- Signup button - only shows when showSignupButton prop is true -->
          <q-btn v-if="showSignupButton" flat dense class="image-panel__signup-btn" label="Sign up today" no-caps @click="handleSignupClick" />
        </div>

        <div class="image-panel__features">
          <q-chip v-for="(feature, index) in features" :key="feature" :class="['image-panel__feature-chip', `image-panel__feature-chip--${chipColors[index % chipColors.length]}`]">
            {{ feature }}
          </q-chip>
        </div>

        <div class="image-panel__gallery">
          <div class="image-panel__image-container">
            <div class="image-panel__image-wrapper" v-for="(img, index) in imageUrls" :key="index">
              <img :src="img" class="image-panel__image" :alt="`Image ${index + 1}`" />
            </div>
          </div>
        </div>
      </div>

      <div class="image-panel__partners">
        <p class="image-panel__partners-title">Trusted by thousands of brands worldwide</p>
        <div class="image-panel__partner-logos">
          <img v-for="(logo, index) in partnerLogos" :key="index" :src="logo.src" :alt="logo.alt" :class="['image-panel__partner-logo', logo.class]" />
        </div>
      </div>
    </div>
  </div>
  <div v-else class="image-panel image-panel--loading">
    <q-spinner size="50px" color="primary" />
  </div>
</template>

<script>
import TypingAnimation from './TypingAnimation.vue';

export default {
  name: 'ImagePanel',
  components: {
    TypingAnimation
  },
  props: {
    showSignupButton: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      contentLoaded: false,
      assetsLoaded: 0,
      totalAssets: 0,
      imageCache: null, // Cache for preloaded images
      logoUrl: require('@/assets/logo.png'),
      quotes: [
        {
          before: 'Ready to',
          highlight: 'create',
          after: 'stunning content?'
        },
        {
          before: 'Ready to',
          highlight: 'share',
          after: 'stunning content?'
        },
        {
          before: 'Ready to',
          highlight: 'edit',
          after: 'stunning content?'
        },
        {
          before: 'Ready to',
          highlight: 'manage',
          after: 'stunning content?'
        }
      ],
      chipColors: ['yellow', 'blue', 'purple', 'green'],
      features: ['Social Media Tools', 'AI Editing Tools', 'Workflow Integrations', 'Content Management'],
      imageUrls: [require('@/assets/foodedit-min.jpeg'), require('@/assets/2W0A5374-min.jpg'), require('@/assets/DSC07355-min.jpg')],
      partnerLogos: [
        { src: require('@/assets/uber.svg'), alt: 'Uber', class: 'image-panel__partner-logo--uber' },
        { src: require('@/assets/sephora.svg'), alt: 'Sephora', class: '' },
        { src: require('@/assets/IHG.svg'), alt: 'IHG', class: '' },
        { src: require('@/assets/mohg.svg'), alt: 'MOHG', class: '' },
        { src: require('@/assets/rclogo.svg'), alt: 'RC Logo', class: '' }
      ]
    };
  },
  created() {
    // Use requestIdleCallback for better performance
    if (window.requestIdleCallback) {
      window.requestIdleCallback(() => {
        this.preloadAllAssets();
      });
    } else {
      // Fallback for browsers that don't support requestIdleCallback
      setTimeout(() => {
        this.preloadAllAssets();
      }, 100);
    }
  },
  methods: {
    preloadAllAssets() {
      // Count all assets to preload
      this.totalAssets = 1 + this.imageUrls.length + this.partnerLogos.length; // logo + gallery images + partner logos

      // Preload logo
      this.preloadImage(this.logoUrl);

      // Preload gallery images
      this.imageUrls.forEach(url => {
        this.preloadImage(url);
      });

      // Preload partner logos
      this.partnerLogos.forEach(logo => {
        this.preloadImage(logo.src);
      });
    },
    preloadImage(src) {
      // Check if image is already in cache
      if (this.imageCache && this.imageCache.has(src)) {
        this.assetsLoaded++;
        if (this.assetsLoaded >= this.totalAssets) {
          this.contentLoaded = true;
        }
        return;
      }

      const img = new Image();
      img.onload = () => {
        // Add to cache
        if (!this.imageCache) this.imageCache = new Set();
        this.imageCache.add(src);

        this.assetsLoaded++;
        if (this.assetsLoaded >= this.totalAssets) {
          this.contentLoaded = true;
        }
      };
      img.onerror = () => {
        console.error(`Failed to load image: ${src}`);
        this.assetsLoaded++;
        if (this.assetsLoaded >= this.totalAssets) {
          this.contentLoaded = true;
        }
      };
      img.src = src;
    },
    handleSignupClick() {
      // Navigate to signup page
      this.$router.push({ name: 'authRegister' });
    }
  }
};
</script>

<style lang="scss" scoped>
.image-panel {
  width: 65%;
  display: flex;
  align-items: center;
  min-height: 100vh;
  background-color: var(--main-bg-color);

  &--loading {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  @media (max-width: 1200px) {
    width: 60%;
  }

  @media (max-width: 768px) {
    display: none; // Hide the ad panel on mobile
  }

  &__content {
    width: 100%;
    height: 100%;
    max-width: 700px;
    min-height: 600px;
    display: flex;
    flex-direction: column;
    padding: 48px 0;
    justify-content: space-between;
  }

  &__logo {
    height: 50px;
    width: auto;
    min-width: 120px;
    align-self: flex-start;
    margin-bottom: 16px;
  }

  &__main-content {
    display: flex;
    flex-direction: column;
    gap: 12px;
    flex-grow: 1;
    margin-bottom: 16px;
  }

  &__headline {
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-self: stretch;
    width: 100%;
  }

  &__title {
    @include mona-sans-font($size: 36px, $bolded: true, $color: var(--main-text-color));
    text-align: left;
    margin: 0;
    line-height: 1.2;
  }

  &__subtitle {
    @include inter-font($size: 16px, $color: var(--secondary-text-color));
    text-align: left;
    margin: 0;
  }

  &__signup-btn {
    width: 100%;
    height: 48px;
    background-color: var(--main-btn-color);
    @include inter-font($size: 16px, $bolded: true, $color: var(--main-text-color));
    margin-top: 8px;
    align-self: flex-start;
    border-radius: 8px;
    padding: 0 24px;
  }

  &__features {
    display: flex;
    gap: 12px;
    margin-bottom: 12px;
    padding-left: 0;
    align-self: stretch;
    width: 100%;
    flex-wrap: wrap;
  }

  &__feature-chip {
    @include mona-sans-font($size: 13px, $bolded: true);
    padding: 6px 12px;
    border-radius: 12px;
    margin-left: 0;
    height: auto;

    &--yellow {
      background-color: rgba(254, 213, 89, 0.15);
      color: darken(#fed559, 30%);
    }

    &--blue {
      background-color: rgba(52, 152, 219, 0.15);
      color: darken(#3498db, 20%);
    }

    &--purple {
      background-color: rgba(91, 45, 144, 0.15);
      color: darken(#5b2d90, 10%);
    }

    &--green {
      background-color: rgba(46, 204, 113, 0.15);
      color: darken(#2ecc71, 20%);
    }
  }

  &__gallery {
    flex-grow: 1;
    height: auto;
    min-height: 220px;
    width: 100%;
    padding: 8px 0 16px 0;
    margin: 0 0 8px 0;
    align-self: stretch;
  }

  &__image-container {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-gap: 12px;
    height: 100%;
  }

  &__image-wrapper {
    position: relative;
    width: 100%;
    height: 100%;
  }

  &__image {
    width: 100%;
    height: 100%;
    min-height: 120px;
    object-fit: cover;
    border-radius: 8px;
    border: 1px solid var(--light-grey-hr);
    background-color: rgba(0, 0, 0, 0.05);
  }

  &__partners {
    margin-top: 0;
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
  }

  &__partners-title {
    @include mona-sans-font($size: 16px, $bolded: true, $color: var(--main-text-color));
    text-align: left;
    margin: 0;
  }

  &__partner-logos {
    margin-top: 8px;
    display: flex;
    width: 100%;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 20px 30px;
  }

  &__partner-logo {
    width: auto;
    height: 40px;
    max-height: 40px;
    min-width: 50px;
    max-width: 180px;
    opacity: 0.9;
    object-fit: contain;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 1;
    }

    &--uber {
      max-width: 70px;
    }
  }
}
</style>
